// @file Surface post properties store
import {
  ALERT_ICON,
  CONFIRM_DELETE_CUSTOM_TEXT_FIELD,
  CONFIRM_DISCARD_SETTING_CHANGES,
} from '@@/bits/confirmation_dialog'
import {
  areAllImagePickerSourcesDisabledFromSettings,
  ContentPickerSource,
  DEVICE_UPLOADABLE_CONTENT_SOURCES,
} from '@@/bits/content_picker'
import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getDiff } from '@@/bits/object_diff_helper'
import type { VideoLengthLimitOption } from '@@/bits/post_properties'
import {
  ATTACHMENT_TEXT_CHAR_LIMIT,
  BODY_PLACEHOLDER_CHAR_LIMIT,
  hasCustomProperties,
  hasRespondablePostProperties,
  isWallCustomPostPropertyOfType,
  shouldPostsBeDeletedAfterRemovingCustomProperty,
  shouldPostsBeDeletedAfterRemovingCustomPropertyOption,
  SUBJECT_PLACEHOLDER_CHAR_LIMIT,
  VideoLengthLimits,
} from '@@/bits/post_properties'
import { tailwindFullConfig } from '@@/bits/tailwind_config'
import { WallWishGroupByTypes, WallWishGroupByValue } from '@@/enums'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import {
  OzConfirmationDialogBoxButtonScheme,
  useGlobalConfirmationDialogStore,
} from '@@/pinia/global_confirmation_dialog'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useScreenReaderNotificationsStore } from '@@/pinia/screen_reader_notifications'
import { useScreenRecorderStore } from '@@/pinia/screen_recorder_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useContentPickerAvRecorderStore } from '@@/pinia/useContentPickerAvRecorderStore'
import { WallCustomPostPropertiesApi } from '@@/surface/api/wall_custom_post_properties'
import { WallPostPropertiesApi } from '@@/surface/api/wall_post_properties'
import type {
  AttachmentTypeSettingsPermissions,
  HashId,
  Post,
  PostColor,
  SingleSelectOption,
  TranslatedCustomPostProperty,
  TranslatedSelectionOption,
  VideoLengthLimit,
  WallCustomPostProperty,
  WallCustomPostSingleSelectProperty,
  WallPostProperties,
} from '@@/types'
import { SettingsSubpanel } from '@@/vuexstore/modules/surface_settings'
import { isEqual } from 'es-toolkit'
import { defineStore } from 'pinia'
import { computed, readonly, ref } from 'vue'

const MIN_SINGLE_SELECT_OPTIONS = 1
const MAX_SINGLE_SELECT_OPTIONS = 100
const MAX_SINGLE_SELECT_OPTION_TEXT_LENGTH = 100
const SINGLE_SELECT_SORT_INDEX_SPACING = 1000

enum PostPropertiesSubpanel {
  SubjectField = 'subjectField',
  AttachmentField = 'attachmentField',
  BodyField = 'bodyField',
}

const useSurfacePostPropertiesStore = defineStore('surfacePostProperties', () => {
  const surfaceStore = useSurfaceStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceSettingsStore = useSurfaceSettingsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
  const contentPickerStore = useContentPickerStore()
  const screenReaderNotificationStore = useScreenReaderNotificationsStore()
  const expandedPostStore = useExpandedPostStore()
  const surfaceTranslationsStore = useSurfaceTranslationsStore()
  const globalAlertDialogStore = useGlobalAlertDialogStore()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
  const screenRecorderStore = useScreenRecorderStore()
  const contentPickerAvRecorderStore = useContentPickerAvRecorderStore()

  const hasFetchedInitialCustomPostProperties = ref<boolean>(false)

  // #region Z-index overrides for comment drawer
  const commentDrawerZIndexOverride = ref<number | null>(null)
  // #endregion

  // #region Panel visibility
  const openedDirectly = ref(false) // false if opened from settings panel, true if opened directly skipping the settings panel
  const xVideoRecorderSettingsPanel = ref(false)

  const openVideoRecorderSettingsPanel = (): void => {
    xVideoRecorderSettingsPanel.value = true
  }

  const hideVideoRecorderSettingsPanel = (): void => {
    xVideoRecorderSettingsPanel.value = false
    if (openedDirectly.value) {
      openedDirectly.value = false
    }
  }

  const xScreenRecorderSettingsPanel = ref(false)

  const openScreenRecorderSettingsPanel = (): void => {
    xScreenRecorderSettingsPanel.value = true
  }

  const hideScreenRecorderSettingsPanel = (): void => {
    xScreenRecorderSettingsPanel.value = false
    if (openedDirectly.value) {
      openedDirectly.value = false
    }
  }

  const xPhotoBoothSettingsPanel = ref(false)

  const openPhotoBoothSettingsPanel = (): void => {
    xPhotoBoothSettingsPanel.value = true
  }

  const hidePhotoBoothSettingsPanel = (): void => {
    xPhotoBoothSettingsPanel.value = false
    if (openedDirectly.value) {
      openedDirectly.value = false
    }
  }
  // #endregion

  /** anywhere -> post properties panel */
  function showSettingsPanelDirectly({
    settingsSubpanel = SettingsSubpanel.PostProperties,
    xMainPanel = true,
    postPropertiesSubpanel,
    aboveContentPicker,
    aboveExpandedPost,
    aboveScreenRecorder,
    aboveCombinedAvRecorder,
    showVideoRecorderSettingsSubpanel,
    showScreenRecorderSettingsSubpanel,
    showPhotoBoothSettingsSubpanel,
  }: {
    settingsSubpanel?: SettingsSubpanel
    xMainPanel?: boolean
    postPropertiesSubpanel?: PostPropertiesSubpanel
    aboveContentPicker?: boolean
    aboveExpandedPost?: boolean
    aboveScreenRecorder?: boolean
    aboveCombinedAvRecorder?: boolean
    showVideoRecorderSettingsSubpanel?: boolean
    showScreenRecorderSettingsSubpanel?: boolean
    showPhotoBoothSettingsSubpanel?: boolean
  } = {}): void {
    if (xMainPanel) {
      surfaceSettingsStore.showSettingsPanel(settingsSubpanel)
    }
    if (postPropertiesSubpanel != null) {
      activePostPropertiesSubpanel.value = postPropertiesSubpanel
    }
    if (aboveContentPicker === true) {
      // show the settings/post properties panel above the content picker
      // set content picker's z-index to 1 less than the settings panel
      // note: this only works if we always render content pickers as siblings of the settings panel
      // ie as direct children of `#surface-panels`: <Teleport to="#surface-panels"><ContentPicker/></Teleport>
      // NOTE: This causes a repaint of <ContentPicker> and all its children. It can make animation reruns for example.
      // This is a side effect that we should be aware of.
      contentPickerStore.setZIndexOverride(tailwindFullConfig.theme.zIndex.sidepanel - 1)
    }
    // show the post properties panel above the expanded post panel, setting its z-index to 1 less than the settings panel
    if (aboveExpandedPost === true) {
      expandedPostStore.setPanelZIndexOverride(tailwindFullConfig.theme.zIndex.sidepanel - 1)
    }
    if (aboveScreenRecorder === true) {
      screenRecorderStore.setZIndexOverride(tailwindFullConfig.theme.zIndex.sidepanel - 1)
    }
    if (aboveCombinedAvRecorder === true) {
      contentPickerAvRecorderStore.setZIndexOverride(tailwindFullConfig.theme.zIndex.sidepanel - 1)
      commentDrawerZIndexOverride.value = tailwindFullConfig.theme.zIndex.sidepanel - 1
    }
    if (showVideoRecorderSettingsSubpanel === true) {
      openVideoRecorderSettingsPanel()
    } else if (showScreenRecorderSettingsSubpanel === true) {
      openScreenRecorderSettingsPanel()
    } else if (showPhotoBoothSettingsSubpanel === true) {
      openPhotoBoothSettingsPanel()
    }
    openedDirectly.value = true
  }

  /** close post properties main panel -> back to settings main panel OR close entire settings panel */
  function closePostPropertiesPanel(): void {
    if (openedDirectly.value) {
      // Reverse of logic in showSettingsPanelDirectly
      openedDirectly.value = false

      activePostPropertiesSubpanel.value = undefined
      contentPickerStore.setZIndexOverride(null)
      expandedPostStore.setPanelZIndexOverride(null)
      screenRecorderStore.setZIndexOverride(null)
      contentPickerAvRecorderStore.setZIndexOverride(null)
      commentDrawerZIndexOverride.value = null

      hideVideoRecorderSettingsPanel()
      hideScreenRecorderSettingsPanel()
      hidePhotoBoothSettingsPanel()

      surfaceSettingsStore.hideSettingsPanel()
    } else {
      surfaceSettingsStore.goBackToMainPanel()
    }
  }

  // #region Subpanel operations
  const activePostPropertiesSubpanel = ref<PostPropertiesSubpanel>()

  function goBackToPostPropertiesPanel(): void {
    activePostPropertiesSubpanel.value = undefined
  }

  /** Close subpanel -> back to post properties panel OR close entire settings panel */
  function closePostPropertiesSubpanel(): void {
    goBackToPostPropertiesPanel()
    if (openedDirectly.value) {
      closePostPropertiesPanel()
    }
  }

  async function saveSettingsAndClosePostPropertiesSubpanel(): Promise<void> {
    return await saveSettings({ onSuccess: () => closePostPropertiesSubpanel() })
  }

  async function discardChangesAndClosePostPropertiesSubpanel(): Promise<void> {
    if (isDirty.value) {
      void globalConfirmationDialogStore.openConfirmationDialog({
        ...CONFIRM_DISCARD_SETTING_CHANGES,
        ...ALERT_ICON,
        afterDiscardActions: [
          () => (previewWallPostProperties.value = wallPostProperties.value),
          () => closePostPropertiesSubpanel(),
        ],
        afterConfirmActions: [async () => await saveSettingsAndClosePostPropertiesSubpanel()],
        buttonScheme: arePostPropertiesInvalid.value
          ? OzConfirmationDialogBoxButtonScheme.DiscardCancel
          : OzConfirmationDialogBoxButtonScheme.SaveDiscardCancel,
        forceFullWidthButtons: true,
        xShadow: true,
      })
    } else {
      previewWallPostProperties.value = wallPostProperties.value
      closePostPropertiesSubpanel()
    }
  }
  // #endregion

  // #region Custom post properties panel
  const xCustomPostPropertiesPanel = ref(false)
  const customPropertiesPanelOpenedDirectly = ref(false)
  const fixedCustomPropertyDataType = ref<WallCustomPostProperty['data_type'] | undefined>(undefined)
  const onCustomPropertyCreated = ref<((property: WallCustomPostProperty) => void) | undefined>(undefined)

  function openCustomPostPropertiesPanel(
    selectedCustomPostProperty?: WallCustomPostProperty,
    options?: {
      fixedDataType?: WallCustomPostProperty['data_type']
      onCreated?: (property: WallCustomPostProperty) => void
      openedDirectly?: boolean
    },
  ): void {
    if (selectedCustomPostProperty != null) {
      activeCustomPostProperty.value = selectedCustomPostProperty
    }
    if (options?.fixedDataType != null) {
      fixedCustomPropertyDataType.value = options.fixedDataType
    }
    if (options?.onCreated != null) {
      const callback = options.onCreated
      onCustomPropertyCreated.value = (property) => {
        callback(property)
        onCustomPropertyCreated.value = undefined
      }
    }
    // Set to true when opened directly (show close icon in the panel header)
    customPropertiesPanelOpenedDirectly.value = options?.openedDirectly ?? false
    xCustomPostPropertiesPanel.value = true
  }

  function closeCustomPostPropertiesPanel(): void {
    activeCustomPostProperty.value = null
    fixedCustomPropertyDataType.value = undefined
    onCustomPropertyCreated.value = undefined
    customPropertiesPanelOpenedDirectly.value = false

    xCustomPostPropertiesPanel.value = false
  }
  // #endregion

  async function saveSettings(options?: { onSuccess?: () => void }): Promise<void> {
    try {
      if (!isDirty.value) return
      const wallPostPropertiesDiff = getDiff<WallPostProperties>(
        wallPostProperties.value,
        previewWallPostProperties.value,
      )
      const postProperties = await WallPostPropertiesApi.updateWallPostProperties(
        surfaceStore.wallId,
        wallPostPropertiesDiff,
      )
      setWallPostProperties(postProperties)
      options?.onSuccess?.()
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'savePostPropertiesSettings' })
    }
  }

  async function saveSettingsAndGoBackToMainPanel(): Promise<void> {
    return await saveSettings({ onSuccess: () => surfaceSettingsStore.goBackToMainPanel() })
  }

  function discardChangesAndGoBackToMainPanel(): void {
    if (isDirty.value) {
      void globalConfirmationDialogStore.openConfirmationDialog({
        ...CONFIRM_DISCARD_SETTING_CHANGES,
        ...ALERT_ICON,
        afterDiscardActions: [
          () => (previewWallPostProperties.value = wallPostProperties.value),
          () => closeCustomPostPropertiesPanel(),
        ],
        afterConfirmActions: [async () => await saveSettingsAndGoBackToMainPanel()],
        buttonScheme: OzConfirmationDialogBoxButtonScheme.SaveDiscardCancel,
        forceFullWidthButtons: true,
        xShadow: true,
      })
    } else {
      previewWallPostProperties.value = wallPostProperties.value
      surfaceSettingsStore.goBackToMainPanel()
    }
  }

  async function saveSettingsAndCloseSettingsPanel(): Promise<void> {
    return await saveSettings({ onSuccess: () => surfaceSettingsStore.hideSettingsPanel() })
  }

  function discardChangesAndCloseSettingsPanel(): void {
    if (isDirty.value) {
      void globalConfirmationDialogStore.openConfirmationDialog({
        ...CONFIRM_DISCARD_SETTING_CHANGES,
        ...ALERT_ICON,
        afterDiscardActions: [
          () => (previewWallPostProperties.value = wallPostProperties.value),
          () => surfaceSettingsStore.hideSettingsPanel(),
        ],
        afterConfirmActions: [async () => await saveSettingsAndCloseSettingsPanel()],
        buttonScheme: OzConfirmationDialogBoxButtonScheme.SaveDiscardCancel,
        forceFullWidthButtons: true,
        xShadow: true,
      })
    } else {
      previewWallPostProperties.value = wallPostProperties.value
      surfaceSettingsStore.hideSettingsPanel()
    }
  }

  // #region Predefined post properties
  const wallPostProperties = ref<WallPostProperties>({
    subject_placeholder: null,
    body_placeholder: null,
    subject_enabled: true,
    attachment_settings: true,
    attachment_text: null,
    body_enabled: true,
    color_enabled: true,
    location_enabled: false,
    ar_lenses_enabled: true,
    required_fields: {
      subject: false,
      attachment: false,
      body: false,
    },
    video_length_limit: null,
  })
  const previewWallPostProperties = ref<WallPostProperties>(wallPostProperties.value)

  const subjectPlaceholder = computed(() => previewWallPostProperties.value.subject_placeholder ?? '')
  const bodyPlaceholder = computed(() => previewWallPostProperties.value.body_placeholder ?? '')
  const isSubjectEnabled = computed(() => previewWallPostProperties.value.subject_enabled)
  const attachmentSettings = computed(() => previewWallPostProperties.value.attachment_settings)
  const attachmentsEnabled = computed(() => attachmentSettings.value !== false)
  const attachmentText = computed(() => previewWallPostProperties.value.attachment_text ?? '')
  const isBodyEnabled = computed(() => previewWallPostProperties.value.body_enabled)
  const isColorEnabled = computed(() => previewWallPostProperties.value.color_enabled)
  const isLocationEnabled = computed(() => previewWallPostProperties.value.location_enabled)
  const isArLensesEnabled = computed(() => {
    // Disable AR lenses for:
    // - nb27.padlet.org https://app.frontapp.com/open/cnv_o36yvyn?key=H6JPvlnRUAuW4NeHiuReSNt08rLWXeeS
    // - hewlettwoodmereufsd.padlet.org https://app.frontapp.com/open/cnv_or9xe4v?key=uSY5VsOFN1a7WKNLQ5deZIGl3D8Yba5f
    if (surfaceStore.tenantId != null && [118774, 79969].includes(surfaceStore.tenantId)) {
      return false
    }
    return previewWallPostProperties.value.ar_lenses_enabled
  })
  const requiredPostProperties = computed(() => previewWallPostProperties.value.required_fields)

  const isDirty = computed(() => {
    return !isEqual(previewWallPostProperties.value, wallPostProperties.value)
  })
  const arePostPropertiesInvalid = computed(
    () =>
      subjectPlaceholder.value.length > SUBJECT_PLACEHOLDER_CHAR_LIMIT ||
      bodyPlaceholder.value.length > BODY_PLACEHOLDER_CHAR_LIMIT ||
      attachmentText.value.length > ATTACHMENT_TEXT_CHAR_LIMIT ||
      (!isSubjectEnabled.value && attachmentSettings.value === false && !isBodyEnabled.value),
  )

  const shouldHandleDragAndDropToPost = computed(() => attachmentSettings.value !== false)
  const shouldHandlePasteToAddAttachment = computed(() => attachmentSettings.value !== false)
  const shouldHandleFileUpload = computed(() => isAttachmentTypeEnabled(ContentPickerSource.FileBrowser))
  const shouldHandleLinks = computed(() => isAttachmentTypeEnabled(ContentPickerSource.LinkInput))

  function showSnackbarForDemoPadletOnboarding(): void {
    globalSnackbarStore.setSnackbar({
      message: __('Cannot edit post properties during the onboarding'),
      notificationType: SnackbarNotificationType.error,
      timeout: 5000,
    })
  }

  function updatePreviewWallPostProperty(
    property: keyof WallPostProperties,
    value: WallPostProperties[keyof WallPostProperties],
  ): void {
    if (surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop) {
      showSnackbarForDemoPadletOnboarding()
      return
    }
    // Only use null to indicate that the placeholder is not set.
    const normalizedValue = typeof value === 'string' && value === '' ? null : value

    // Handle photo album setting when attachment settings are updated
    if (property === 'attachment_settings' && typeof normalizedValue === 'object' && normalizedValue !== null) {
      // Check if all image picker types are being disabled
      const newAttachmentSettings = normalizedValue as Record<ContentPickerSource, boolean>
      const areAllImagePickerSourcesDisabledInNewSettings =
        areAllImagePickerSourcesDisabledFromSettings(newAttachmentSettings)

      // If all image picker types are disabled, also disable photo album
      if (areAllImagePickerSourcesDisabledInNewSettings) {
        newAttachmentSettings[ContentPickerSource.PhotoAlbum] = false
      }

      previewWallPostProperties.value = {
        ...previewWallPostProperties.value,
        [property]: newAttachmentSettings,
      }
    } else {
      previewWallPostProperties.value = {
        ...previewWallPostProperties.value,
        [property]: normalizedValue,
      }
    }
  }

  const originalIsScreenRecorderAttachmentSetting = ref<boolean>(false)
  const setOriginalIsScreenRecorderAttachmentSetting = (): void => {
    originalIsScreenRecorderAttachmentSetting.value =
      typeof wallPostProperties.value.attachment_settings === 'boolean'
        ? wallPostProperties.value.attachment_settings
        : wallPostProperties.value.attachment_settings[ContentPickerSource.ScreenshareRecorder]
  }

  function setWallPostProperties(
    properties: Partial<WallPostProperties>,
    saveScreenRecorderAttachmentSetting = true,
  ): void {
    wallPostProperties.value = {
      ...wallPostProperties.value,
      ...properties,
      attachment_settings: properties.attachment_settings ?? wallPostProperties.value.attachment_settings,
    }
    previewWallPostProperties.value = wallPostProperties.value
    if (saveScreenRecorderAttachmentSetting) {
      setOriginalIsScreenRecorderAttachmentSetting()
    }
  }

  function setWallPostPropertiesScreenRecorderAttachmentSetting(enabled: boolean): void {
    if (typeof wallPostProperties.value.attachment_settings === 'boolean') {
      return
    }
    setWallPostProperties(
      {
        attachment_settings: {
          ...wallPostProperties.value.attachment_settings,
          [ContentPickerSource.ScreenshareRecorder]: enabled,
        },
      },
      false,
    )
  }

  function restoreWallPostPropertiesScreenRecorderAttachmentSetting(): void {
    setWallPostPropertiesScreenRecorderAttachmentSetting(originalIsScreenRecorderAttachmentSetting.value)
  }

  function getDisplayedColorForPost(post: Post): PostColor {
    return isColorEnabled.value ? post.color : null
  }

  function isAttachmentTypeEnabled(source: ContentPickerSource): boolean {
    if (attachmentSettings.value === true) return true
    if (source === ContentPickerSource.DeviceUploads) {
      if (!isAppUsing('deviceUploads')) return false
      // Device Uploads is special in that it is a proxy for other content
      // sources. If any of the admissible content sources are enabled, then
      // Device Uploads is enabled.
      return DEVICE_UPLOADABLE_CONTENT_SOURCES.some((source) => attachmentSettings.value[source] === true)
    }
    return attachmentSettings.value[source] === true
  }
  // #endregion

  // #region video length limits
  const wallVideoLengthLimit = computed(() => surfaceStore.wallAttributes.video_length_limit as number) // current wall's max limit based on plan
  const videoLengthLimit = computed(() => previewWallPostProperties.value.video_length_limit) // current wall's limit set by user

  const maxPersonalWallVideoLengthLimit = 15 * 60 // see Quotas.rb
  const maxLibraryWallVideoLengthLimit = 30 * 60 // see LibraryQuotas.rb

  const maxPossibleVideoLengthLimit = computed(() => {
    if (surfaceStore.isLibraryWall) {
      return maxLibraryWallVideoLengthLimit
    }
    return maxPersonalWallVideoLengthLimit
  })

  const currentVideoLengthLimit = computed(() => {
    if (
      // No time limit set => return wall's max time limit.
      videoLengthLimit.value == null ||
      // Time limit set is higher than wall's max time limit => return wall's max time limit.
      // This can happen if the builder downgraded their plan.
      videoLengthLimit.value > wallVideoLengthLimit.value
    ) {
      return wallVideoLengthLimit.value
    }
    return videoLengthLimit.value
  })

  const currentVideoLengthLimitSetting = computed(() => {
    return VideoLengthLimits.find(
      (lengthLimit) => lengthLimit.value === currentVideoLengthLimit.value,
    ) as VideoLengthLimitOption
  })

  const updateVideoLengthLimit = (limit: VideoLengthLimit): void =>
    updatePreviewWallPostProperty('video_length_limit', limit)
  // #endregion

  // #region Custom post properties
  const wallCustomPostPropertiesRawRef = ref<WallCustomPostProperty[]>([])

  const rawWallCustomPostProperties = computed<WallCustomPostProperty[]>(() => {
    return [...wallCustomPostPropertiesRawRef.value].sort((propA, propB) => {
      // link buttons should always be last
      if (isWallCustomPostPropertyOfType(propA, 'link_button')) return 1
      if (isWallCustomPostPropertyOfType(propB, 'link_button')) return -1

      return propA.sort_index - propB.sort_index
    })
  })

  const wallCustomPostProperties = computed<WallCustomPostProperty[]>(() => {
    const surfaceTranslationsStore = useSurfaceTranslationsStore()
    const properties = rawWallCustomPostProperties.value

    // Return unmodified properties if not viewing translations or translated data is missing
    if (
      !surfaceTranslationsStore.isViewingTranslatedPadlet ||
      (surfaceTranslationsStore.translatedWall?.customPostProperties == null &&
        surfaceTranslationsStore.translatedWall?.selectionOptions == null)
    ) {
      return properties
    }

    // Create a map for translated property names (propertyId -> name)
    const translatedPropertiesMap = (surfaceTranslationsStore.translatedWall.customPostProperties ?? []).reduce<
      Map<string, string>
    >((map, prop: TranslatedCustomPostProperty) => {
      map.set(prop.id, prop.name)
      return map
    }, new Map())

    // Create a nested map for translated selection options (propertyId -> optionId -> name)
    const translatedOptionsMap = (surfaceTranslationsStore.translatedWall.selectionOptions ?? []).reduce<
      Map<string, Map<string, string>>
    >((propertyMap, option: TranslatedSelectionOption) => {
      let optionMap = propertyMap.get(option.propertyId)
      if (optionMap == null) {
        optionMap = new Map<string, string>()
        propertyMap.set(option.propertyId, optionMap)
      }
      optionMap.set(option.id, option.name)
      return propertyMap
    }, new Map())

    // Iterate over the source properties and merge translations
    const translatedProperties = properties.map((property) => {
      if (property.id == null) return property
      // Look up the translated property name
      const translatedPropertyName = translatedPropertiesMap.get(property.id)

      // Start building the potentially translated property object
      const translatedProperty = {
        ...property,
        // Use translated name if available, otherwise fallback to original
        ...(translatedPropertyName !== undefined && { name: translatedPropertyName }),
      }

      // Handle single select properties specifically
      if (isWallCustomPostPropertyOfType(property, 'single_select')) {
        // Look up the map of translated options for this specific property
        const propertyOptionsMap = translatedOptionsMap.get(property.id)
        const translatedSingleSelectProperty = translatedProperty as WallCustomPostSingleSelectProperty

        if (propertyOptionsMap != null) {
          // Map over the original options and merge translated names
          translatedSingleSelectProperty.selection_options = property.selection_options.map((option) => {
            if (option.id == null) return option
            const translatedOptionName = propertyOptionsMap.get(option.id)
            return {
              ...option,
              // Use translated name if available, otherwise fallback to original
              ...(translatedOptionName !== undefined && { name: translatedOptionName }),
            }
          })
        }
      }

      return translatedProperty
    })

    // Sort the translated properties such that link buttons are always last
    const sortedTranslatedProperties = [...translatedProperties].sort((propA, propB) => {
      if (isWallCustomPostPropertyOfType(propA, 'link_button')) return 1
      if (isWallCustomPostPropertyOfType(propB, 'link_button')) return -1

      return propA.sort_index - propB.sort_index
    })

    return sortedTranslatedProperties
  })

  const rawWallCustomPostPropertiesById = computed<Record<string, WallCustomPostProperty>>(() => {
    const options = {}
    wallCustomPostPropertiesRawRef.value.forEach((property) => {
      if (property.id == null) return
      options[property.id] = property
    })
    return options
  })
  const wallCustomPostPropertiesById = computed<Record<string, WallCustomPostProperty>>(() => {
    const options = {}
    wallCustomPostProperties.value.forEach((property) => {
      if (property.id == null) return
      options[property.id] = property
    })
    return options
  })
  const requiredWallCustomPostPropertiesById = computed<Record<string, WallCustomPostProperty>>(() => {
    const options = {}
    wallCustomPostProperties.value.forEach((property) => {
      if (property.id == null || !property.required) return
      options[property.id] = property
    })
    return options
  })
  const wallSingleSelectPropsByCustomPropertyId = computed<Record<string, WallCustomPostSingleSelectProperty>>(() => {
    const options = {}
    Object.entries(wallCustomPostPropertiesById.value).forEach(([customPropId, customProp]) => {
      if (isWallCustomPostPropertyOfType(customProp, 'single_select')) {
        options[customPropId] = customProp
      }
    })

    return options
  })

  const wallSingleSelectDefaultsByCustomPropertyId = computed<Record<string, string | null>>(() => {
    const options = {}
    Object.entries(wallSingleSelectPropsByCustomPropertyId.value).forEach(([customPropId, customProp]) => {
      options[customPropId] = customProp.selection_options.find((option) => option.default)?.id ?? null
    })

    return options
  })

  const attachmentSettingPermissions = ref<AttachmentTypeSettingsPermissions | null>(null)

  function setAttachmentSettingPermissions(newAttachmentSettingPermissions: AttachmentTypeSettingsPermissions): void {
    attachmentSettingPermissions.value = newAttachmentSettingPermissions
  }

  const fetchAndSetAttachmentSettingPermissions = async (libraryHashId: HashId): Promise<void> => {
    try {
      const { WallAttachmentSettingPermissionsApi } = await import('@@/surface/api/wall_attachment_setting_permissions')
      const attachmentSettingPermissions =
        await WallAttachmentSettingPermissionsApi.fetchWallAttachmentSettingPermissions(libraryHashId)
      setAttachmentSettingPermissions(attachmentSettingPermissions)
    } catch (e) {
      captureFetchException(e, { source: 'fetchAndSetAttachmentSettingPermissions' })
    }
  }

  const isContentPickerSourcePermitted = (source: ContentPickerSource): boolean => {
    if (attachmentSettingPermissions.value === null) return true

    // Map ContentPickerSource to AttachmentTypeSettingsPermissions
    const mapSourceToPermission = {
      [ContentPickerSource.FileBrowser]: attachmentSettingPermissions.value.file,
      [ContentPickerSource.LinkInput]: attachmentSettingPermissions.value.link,
      [ContentPickerSource.WebBrowser]: attachmentSettingPermissions.value.webSearch,
      [ContentPickerSource.PhotoBooth]: attachmentSettingPermissions.value.photoBooth,
      [ContentPickerSource.VideoRecorder]: attachmentSettingPermissions.value.videoRecorder,
      [ContentPickerSource.AudioRecorder]: attachmentSettingPermissions.value.audioRecorder,
      [ContentPickerSource.DrawingCanvas]: attachmentSettingPermissions.value.drawing,
      [ContentPickerSource.PadletBrowser]: attachmentSettingPermissions.value.padlet,
      [ContentPickerSource.MapBrowser]: attachmentSettingPermissions.value.locationSearch,
      [ContentPickerSource.ScreenshareRecorder]: attachmentSettingPermissions.value.screenRecorder,
      [ContentPickerSource.AiImageSearch]: attachmentSettingPermissions.value.aiImageSearch,
      [ContentPickerSource.ImageSearch]: attachmentSettingPermissions.value.imageSearch,
      [ContentPickerSource.ThreeDObjectSearch]: attachmentSettingPermissions.value.threeDObjectSearch,
      [ContentPickerSource.StockImageSearch]: attachmentSettingPermissions.value.stockImageSearch,
      [ContentPickerSource.VideoSearch]: attachmentSettingPermissions.value.videoSearch,
      [ContentPickerSource.AudioSearch]: attachmentSettingPermissions.value.audioSearch,
      [ContentPickerSource.GifSearch]: attachmentSettingPermissions.value.gifSearch,
      [ContentPickerSource.Poll]: attachmentSettingPermissions.value.poll,
      [ContentPickerSource.GoogleDrive]: attachmentSettingPermissions.value.googleDrive,
      [ContentPickerSource.MicrosoftOneDrive]: attachmentSettingPermissions.value.microsoftOneDrivePicker,
      [ContentPickerSource.AiTalk]: attachmentSettingPermissions.value.aiTalk,
      [ContentPickerSource.MediaLibrary]: true,
      [ContentPickerSource.PhotoAlbum]: attachmentSettingPermissions.value.photoAlbum,
    }
    // MediaLibrary is only available on mobile, so always permit on web,
    // otherwise isAllPermittedAttachmentsEnabled will return false even when all web attachments are enabled.

    return Boolean(mapSourceToPermission[source])
  }

  const isAllPermittedAttachmentsEnabled = computed(() => {
    if (attachmentSettings.value === true) return true
    if (attachmentSettings.value === false) return false

    if (
      attachmentSettingPermissions.value !== null &&
      Object.entries(attachmentSettings.value).every(
        ([source, enabled]) =>
          !isContentPickerSourcePermitted(source as ContentPickerSource) || // if source isn't permitted, we don't care if it's enabled/disabled
          enabled, // source is enabled
      )
    ) {
      return true
    }

    return false
  })

  const activeCustomPostProperty = ref<WallCustomPostProperty | null>(null)
  const isCreatingCustomPostProperty = ref(false)
  const isUpdatingCustomPostProperty = ref(false)
  const isDeletingCustomPostProperty = ref(false)
  const customLinkButtonProperty = computed(() =>
    wallCustomPostProperties.value.find((property) => isWallCustomPostPropertyOfType(property, 'link_button')),
  )

  function shouldShowPostProperties(post: Post): boolean {
    // has location or at least 1 custom property or at least 1 custom property response
    return (
      post.location_name != null ||
      hasCustomProperties(post) ||
      hasRespondablePostProperties(wallCustomPostProperties.value)
    )
  }

  async function createCustomPostProperty(customPostPropertyData: WallCustomPostProperty): Promise<void> {
    if (surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop) {
      showSnackbarForDemoPadletOnboarding()
      return
    }

    // The onCustomPropertyCreated callback gets cleared if the custom property panel is closed,
    // which happens before the async create custom property call resolves.
    // Need to store the reference here before we call async code!
    const callback = onCustomPropertyCreated.value

    try {
      isCreatingCustomPostProperty.value = true
      const customPostProperty = await WallCustomPostPropertiesApi.createWallCustomPostProperty(
        surfaceStore.wallId,
        customPostPropertyData,
      )
      insertNewCustomPostProperty(customPostProperty)

      if (callback) {
        callback(customPostProperty)
      }
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'createCustomPostProperty' })
    } finally {
      isCreatingCustomPostProperty.value = false
    }
  }

  // Returns the updated custom post property or null if the update fails
  async function updateCustomPostProperty(
    customPostPropertyData: WallCustomPostProperty,
  ): Promise<WallCustomPostProperty | null> {
    try {
      // Get the previous property before the update for vote type change detection
      const previousProperty = wallCustomPostPropertiesRawRef.value.find(
        (prop) => prop.id === customPostPropertyData.id,
      )

      isUpdatingCustomPostProperty.value = true
      const customPostProperty = await WallCustomPostPropertiesApi.updateWallCustomPostProperty(
        surfaceStore.wallId,
        customPostPropertyData,
      )
      screenReaderNotificationStore.addScreenReaderMessage(
        __('Post property "%{customField}" has been updated', {
          customField: customPostProperty.name,
        }),
      )
      updateCustomPostPropertyInList(customPostProperty)

      // When vote type changes from 'upvotes_and_downvotes' to 'upvotes_only',
      // the backend deletes all downvote responses. Refetch posts to update the
      // aggregated vote counts (sum/count) displayed in the UI for all posts.
      const shouldRefetchForVoteChange =
        previousProperty != null &&
        isWallCustomPostPropertyOfType(previousProperty, 'vote') &&
        isWallCustomPostPropertyOfType(customPostPropertyData, 'vote') &&
        previousProperty.vote_type === 'upvotes_and_downvotes' &&
        customPostPropertyData.vote_type === 'upvotes_only'

      // When max rating decreases, the backend deletes all rating responses.
      // Refetch posts to update the aggregated rating values (sum/count/average)
      // displayed in the UI for all posts.
      const shouldRefetchForRatingChange =
        previousProperty != null &&
        isWallCustomPostPropertyOfType(previousProperty, 'rating') &&
        isWallCustomPostPropertyOfType(customPostPropertyData, 'rating') &&
        customPostPropertyData.max_rating < previousProperty.max_rating
      // When max score decreases, the backend deletes all score responses.
      // Refetch posts to update the aggregated score values (sum/count/average)
      // displayed in the UI for all posts.
      const shouldRefetchForScoreChange =
        previousProperty != null &&
        isWallCustomPostPropertyOfType(previousProperty, 'score') &&
        isWallCustomPostPropertyOfType(customPostPropertyData, 'score') &&
        customPostPropertyData.max_score < previousProperty.max_score

      if (shouldRefetchForVoteChange || shouldRefetchForRatingChange || shouldRefetchForScoreChange) {
        void surfacePostsStore.fetchPosts()
      }

      return customPostProperty
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'updateCustomPostProperty' })
      return null
    } finally {
      isUpdatingCustomPostProperty.value = false
    }
  }

  async function deleteCustomPostProperty(propertyId: string): Promise<void> {
    try {
      isDeletingCustomPostProperty.value = true
      await WallCustomPostPropertiesApi.deleteWallCustomPostProperty(surfaceStore.wallId, propertyId)
      removeCustomPostPropertyFromList(propertyId)

      if (surfaceWishArrangementStore.isWishGroupedBySingleSelectProperty(propertyId)) {
        surfaceWishArrangementStore.updateWishGroupBy(
          surfaceStore.isShelf ? WallWishGroupByTypes.Section : WallWishGroupByTypes.None,
        )
      }
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'deleteCustomPostProperty' })
    } finally {
      isDeletingCustomPostProperty.value = false
    }
  }

  function removeCustomPostPropertyFromList(propertyId: string): void {
    const targetCustomPostPropIndex = wallCustomPostPropertiesRawRef.value.findIndex(
      (customPostProperty) => customPostProperty.id === propertyId,
    )
    if (targetCustomPostPropIndex !== -1) {
      wallCustomPostPropertiesRawRef.value.splice(targetCustomPostPropIndex, 1)
      screenReaderNotificationStore.addScreenReaderMessage(
        __('Post property "%{customField}" has been deleted', { customField: propertyId }),
      )
    }
  }

  function updateCustomPostPropertyInList(updatedCustomPostProperty: WallCustomPostProperty): void {
    const targetCustomPostPropIndex = wallCustomPostPropertiesRawRef.value.findIndex(
      (customPostProperty) => customPostProperty.id === updatedCustomPostProperty.id,
    )
    const currentCustomPostProperty = wallCustomPostPropertiesRawRef.value[targetCustomPostPropIndex]
    // Translate custom post properties if the name has changed or the selection options have changed
    const hasNameChanged = currentCustomPostProperty.name !== updatedCustomPostProperty.name
    const isSingleSelectAndHaveSelectionOptionsChanged =
      currentCustomPostProperty.data_type === 'single_select' &&
      updatedCustomPostProperty.data_type === 'single_select' &&
      currentCustomPostProperty.selection_options !== updatedCustomPostProperty.selection_options

    if (
      surfaceTranslationsStore.isViewingTranslatedPadlet &&
      (hasNameChanged || isSingleSelectAndHaveSelectionOptionsChanged)
    ) {
      void surfaceTranslationsStore.translateCustomPostProperties()
    }
    if (targetCustomPostPropIndex !== -1) {
      wallCustomPostPropertiesRawRef.value.splice(targetCustomPostPropIndex, 1, updatedCustomPostProperty)
    }
  }

  function setCustomPostProperties(customPostProperties: WallCustomPostProperty[]): void {
    wallCustomPostPropertiesRawRef.value = customPostProperties
  }

  function insertNewCustomPostProperty(customPostProperty: WallCustomPostProperty): void {
    wallCustomPostPropertiesRawRef.value.push(customPostProperty)
    screenReaderNotificationStore.addScreenReaderMessage(
      __('Post property "%{customField}" has been added', { customField: customPostProperty.name }),
    )
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      void surfaceTranslationsStore.translateCustomPostProperties()
    }
  }

  function showDeleteCustomPropertyWarning(): void {
    const propertyId = activeCustomPostProperty.value?.id
    if (propertyId == null) return

    const customField = activeCustomPostProperty.value?.name ?? ''
    const isCustomFieldNameTooLong = customField.length > 300 // from manually testing this length is when dialog box is close to overflowing
    const title = !isCustomFieldNameTooLong
      ? __('Delete "%{customField}" field?', {
          customField,
        })
      : __('Delete this field?')
    let body: string
    if (shouldPostsBeDeletedAfterRemovingCustomProperty(propertyId, surfacePostsStore.allSortedPosts)) {
      body = !isCustomFieldNameTooLong
        ? __(
            'This field and its values will be deleted from every post. Posts with only this field will be deleted. Any values entered into "%{customField}" will not be recoverable. This action is permanent.',
            {
              customField,
            },
          )
        : __(
            'This field and its values will be deleted from every post. Posts with only this field will be deleted. Any values entered into this field will not be recoverable. This action is permanent.',
          )
    } else {
      body = !isCustomFieldNameTooLong
        ? __(
            'This field and its values will be deleted from every post. Any values entered into "%{customField}" will not be recoverable. This action is permanent.',
            {
              customField,
            },
          )
        : __(
            'This field and its values will be deleted from every post. Any values entered into this field will not be recoverable. This action is permanent.',
          )
    }

    void globalConfirmationDialogStore.openConfirmationDialog({
      ...CONFIRM_DELETE_CUSTOM_TEXT_FIELD,
      title,
      body,
      afterConfirmActions: [
        async () => await deleteCustomPostProperty(propertyId),
        () => closeCustomPostPropertiesPanel(),
      ],
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
      xShadow: true,
    })
  }

  // Moves a custom property's sort index up or down.
  async function moveCustomProperty(propertyId: string, direction: 'up' | 'down'): Promise<void> {
    const currentIndex = wallCustomPostProperties.value.findIndex((prop) => prop.id === propertyId)
    if (currentIndex === -1) return

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    if (targetIndex < 0 || targetIndex >= wallCustomPostProperties.value.length) return

    // Calculate new sort index as midpoint between adjacent items
    const { prevItem, nextItem } = (() => {
      if (direction === 'up') {
        return {
          prevItem: wallCustomPostProperties.value[targetIndex - 1],
          nextItem: wallCustomPostProperties.value[targetIndex],
        }
      } else {
        const nextItem = wallCustomPostProperties.value[targetIndex + 1]
        return {
          prevItem: wallCustomPostProperties.value[targetIndex],
          nextItem:
            nextItem?.data_type === 'link_button'
              ? undefined // Ignore next item sort_index if it's a link button
              : nextItem,
        }
      }
    })()

    const newSortIndex = (() => {
      if (prevItem?.sort_index == null) return (nextItem?.sort_index ?? 0) - 10000
      if (nextItem?.sort_index == null) return prevItem.sort_index + 10000
      return Math.floor(prevItem.sort_index + (nextItem.sort_index - prevItem.sort_index) / 2)
    })()

    const currentProperty = wallCustomPostProperties.value[currentIndex]
    const currentPropertyAttributes = { ...currentProperty, sort_index: newSortIndex }

    // Update locally first
    updateCustomPostPropertyInList(currentPropertyAttributes)

    try {
      // Then update on server
      await updateCustomPostProperty(currentPropertyAttributes)
    } catch (e) {
      // Revert on failure
      updateCustomPostPropertyInList(currentProperty)
      throw e
    }
  }
  // #endregion

  // #region Single select properties
  const hasSingleSelectProperty = computed(() => {
    return Object.values(wallSingleSelectPropsByCustomPropertyId.value).length > 0
  })

  function findSingleSelectPropertyOptionId(payload: {
    propertyId: WallCustomPostSingleSelectProperty['id']
    optionId: SingleSelectOption['id']
  }): SingleSelectOption['id'] | null {
    if (payload.propertyId == null) return null

    const singleSelectProperty = wallSingleSelectPropsByCustomPropertyId.value[payload.propertyId]
    if (singleSelectProperty == null) return null

    return singleSelectProperty.selection_options.find((option) => option.id === payload.optionId)?.id ?? null
  }

  function getSingleSelectPropertyOptionName(payload: {
    propertyId: WallCustomPostSingleSelectProperty['id']
    optionId: SingleSelectOption['id']
  }): string | null {
    if (payload.propertyId == null) return null

    const singleSelectProperty = wallSingleSelectPropsByCustomPropertyId.value[payload.propertyId]
    if (singleSelectProperty == null) return null

    return singleSelectProperty.selection_options.find((option) => option.id === payload.optionId)?.name ?? null
  }

  function updateSingleSelectPropertyOptionName(payload: {
    propertyId: WallCustomPostSingleSelectProperty['id']
    optionId: SingleSelectOption['id']
    optionName: SingleSelectOption['name']
  }): void {
    if (payload.propertyId == null) return

    const singleSelectProperty = wallSingleSelectPropsByCustomPropertyId.value[payload.propertyId]
    if (singleSelectProperty == null) return

    void updateCustomPostProperty({
      ...singleSelectProperty,
      selection_options: singleSelectProperty.selection_options.map((option) => {
        if (option.id === payload.optionId) {
          return { ...option, name: payload.optionName }
        }
        return option
      }),
    })
  }

  function updateSingleSelectPropertyOptionAsDefault(payload: {
    propertyId: WallCustomPostSingleSelectProperty['id']
    optionId: SingleSelectOption['id']
  }): void {
    if (payload.propertyId == null) return

    const singleSelectProperty = wallSingleSelectPropsByCustomPropertyId.value[payload.propertyId]
    if (singleSelectProperty == null) return

    void updateCustomPostProperty({
      ...singleSelectProperty,
      selection_options: singleSelectProperty.selection_options.map((option) => {
        return { ...option, default: option.id === payload.optionId }
      }),
    })
  }

  async function createSingleSelectPropertyOptionBefore(payload: {
    propertyId: WallCustomPostSingleSelectProperty['id']
    optionId: SingleSelectOption['id']
  }): Promise<string | null> {
    if (payload.propertyId == null) return null

    const singleSelectProperty = wallSingleSelectPropsByCustomPropertyId.value[payload.propertyId]
    if (singleSelectProperty == null) return null

    const singleSelectPropertyOptions = singleSelectProperty.selection_options
    if (singleSelectPropertyOptions == null) return null

    const newOption = {
      name: __('New option'),
      sort_index: 0, // Will be recalculated below
      default: false,
    }

    let newOptions: SingleSelectOption[] = []

    const currentOptionIndex = singleSelectPropertyOptions.findIndex((option) => option.id === payload.optionId)
    if (payload.optionId === WallWishGroupByValue.NoValue) {
      newOptions = [...singleSelectPropertyOptions, newOption]
    } else {
      if (currentOptionIndex === -1) return null
      newOptions = [...singleSelectPropertyOptions]
      newOptions.splice(currentOptionIndex, 0, newOption)
    }

    // Update sort indices to maintain proper ordering
    newOptions.forEach((option, index) => {
      option.sort_index = index * SINGLE_SELECT_SORT_INDEX_SPACING
    })

    const updatedCustomPostProperty = (await updateCustomPostProperty({
      ...singleSelectProperty,
      selection_options: newOptions,
    })) as WallCustomPostSingleSelectProperty
    const updatedCustomPostPropertyOptions = updatedCustomPostProperty?.selection_options
    if (updatedCustomPostPropertyOptions == null) return null

    const newOptionId =
      payload.optionId === WallWishGroupByValue.NoValue
        ? updatedCustomPostPropertyOptions[updatedCustomPostPropertyOptions.length - 1].id
        : updatedCustomPostPropertyOptions[currentOptionIndex].id
    return newOptionId ?? null
  }

  async function createSingleSelectPropertyOptionAfter(payload: {
    propertyId: WallCustomPostSingleSelectProperty['id']
    optionId: SingleSelectOption['id']
    value?: string
  }): Promise<string | null> {
    if (payload.propertyId == null) return null

    const singleSelectProperty = wallSingleSelectPropsByCustomPropertyId.value[payload.propertyId]
    if (singleSelectProperty == null) return null

    const singleSelectPropertyOptions = singleSelectProperty.selection_options
    if (singleSelectPropertyOptions == null) return null

    const currentOptionIndex = singleSelectPropertyOptions.findIndex((option) => option.id === payload.optionId)
    if (currentOptionIndex === -1) return null

    const newOption = {
      name: payload.value ?? __('New option'),
      sort_index: 0, // Will be recalculated below
      default: false,
    }

    const newOptions = [...singleSelectPropertyOptions]
    newOptions.splice(currentOptionIndex + 1, 0, newOption)

    // Update sort indices to maintain proper ordering
    newOptions.forEach((option, index) => {
      option.sort_index = index * SINGLE_SELECT_SORT_INDEX_SPACING
    })

    const updatedCustomPostProperty = (await updateCustomPostProperty({
      ...singleSelectProperty,
      selection_options: newOptions,
    })) as WallCustomPostSingleSelectProperty

    return updatedCustomPostProperty?.selection_options[currentOptionIndex + 1].id ?? null
  }

  function moveSingleSelectPropertyOptionUp(payload: {
    propertyId: WallCustomPostSingleSelectProperty['id']
    optionId: SingleSelectOption['id']
  }): void {
    if (payload.propertyId == null) return

    const singleSelectProperty = wallSingleSelectPropsByCustomPropertyId.value[payload.propertyId]
    if (singleSelectProperty == null) return

    const singleSelectPropertyOptions = singleSelectProperty.selection_options
    if (singleSelectPropertyOptions == null) return

    const currentOptionIndex = singleSelectPropertyOptions.findIndex((option) => option.id === payload.optionId)
    // Can't move before if already at the first position
    if (currentOptionIndex <= 0) return

    // Create a new array with the option moved before the previous one
    const newOptions = [...singleSelectPropertyOptions]
    const [movedOption] = newOptions.splice(currentOptionIndex, 1)
    newOptions.splice(currentOptionIndex - 1, 0, movedOption)

    // Update sort indices to maintain proper ordering
    newOptions.forEach((option, index) => {
      option.sort_index = index * SINGLE_SELECT_SORT_INDEX_SPACING
    })

    void updateCustomPostProperty({
      ...singleSelectProperty,
      selection_options: newOptions,
    })
  }

  function moveSingleSelectPropertyOptionDown(payload: {
    propertyId: WallCustomPostSingleSelectProperty['id']
    optionId: SingleSelectOption['id']
  }): void {
    if (payload.propertyId == null) return

    const singleSelectProperty = wallSingleSelectPropsByCustomPropertyId.value[payload.propertyId]
    if (singleSelectProperty == null) return

    const singleSelectPropertyOptions = singleSelectProperty.selection_options
    if (singleSelectPropertyOptions == null) return

    const currentOptionIndex = singleSelectPropertyOptions.findIndex((option) => option.id === payload.optionId)
    // Can't move after if already at the last position
    if (currentOptionIndex === -1 || currentOptionIndex === singleSelectPropertyOptions.length - 1) return

    // Create a new array with the option moved after the next one
    const newOptions = [...singleSelectPropertyOptions]
    const [movedOption] = newOptions.splice(currentOptionIndex, 1)
    newOptions.splice(currentOptionIndex + 1, 0, movedOption)

    // Update sort indices to maintain proper ordering
    newOptions.forEach((option, index) => {
      option.sort_index = index * SINGLE_SELECT_SORT_INDEX_SPACING
    })

    void updateCustomPostProperty({
      ...singleSelectProperty,
      selection_options: newOptions,
    })
  }

  function moveSingleSelectPropertyOptionRelativeTo(payload: {
    propertyId: WallCustomPostSingleSelectProperty['id']
    sourceOptionId: SingleSelectOption['id']
    targetOptionId: SingleSelectOption['id']
    relativePosition: 'before' | 'after'
  }): void {
    if (payload.propertyId == null) return

    const singleSelectProperty = wallSingleSelectPropsByCustomPropertyId.value[payload.propertyId]
    if (singleSelectProperty == null) return

    const singleSelectPropertyOptions = singleSelectProperty.selection_options
    if (singleSelectPropertyOptions == null) return

    const sourceOptionIndex = singleSelectPropertyOptions.findIndex((option) => option.id === payload.sourceOptionId)
    const targetOptionIndex = singleSelectPropertyOptions.findIndex((option) => option.id === payload.targetOptionId)

    // Can't move if source or target options are not found
    if (sourceOptionIndex === -1 || targetOptionIndex === -1) return

    // Can't move to the same position
    if (sourceOptionIndex === targetOptionIndex) return

    // Create a new array with the option moved to the target position
    const newOptions = [...singleSelectPropertyOptions]
    const [movedOption] = newOptions.splice(sourceOptionIndex, 1)

    // Calculate the insertion index based on relative position
    let insertionIndex: number
    if (payload.relativePosition === 'before') {
      // If moving down the list, we need to account for the removed element
      insertionIndex = sourceOptionIndex < targetOptionIndex ? targetOptionIndex - 1 : targetOptionIndex
    } else {
      // If moving down the list, we need to account for the removed element
      insertionIndex = sourceOptionIndex < targetOptionIndex ? targetOptionIndex : targetOptionIndex + 1
    }

    newOptions.splice(insertionIndex, 0, movedOption)

    // Update sort indices to maintain proper ordering
    newOptions.forEach((option, index) => {
      option.sort_index = index * SINGLE_SELECT_SORT_INDEX_SPACING
    })

    const updatedProperty = {
      ...singleSelectProperty,
      selection_options: newOptions,
    }

    // Optimistic update: apply changes immediately for responsive drag and drop
    updateCustomPostPropertyInList(updatedProperty)

    // Then update on server
    void updateCustomPostProperty(updatedProperty).then((result) => {
      // If server update fails, revert the optimistic update
      if (result == null) {
        updateCustomPostPropertyInList(singleSelectProperty)
      }
    })
  }

  function onDeleteSingleSelectPropertyOption(payload: {
    propertyId: WallCustomPostSingleSelectProperty['id']
    optionId: SingleSelectOption['id']
  }): void {
    const propertyId = payload.propertyId
    if (propertyId == null) return

    const singleSelectProperty = wallSingleSelectPropsByCustomPropertyId.value[propertyId]
    if (singleSelectProperty == null) return

    // Show alert if this is the last option
    if (singleSelectProperty.selection_options.length === 1) {
      globalAlertDialogStore.openAlertDialog({
        title: __('Group cannot be deleted'),
        body: __('At least one group is required. Please create a new group if you want to delete this one.'),
        closeButtonText: __('OK'),
        shouldFadeIn: true,
      })
      return
    }

    // Show confirmation dialog if deleting this option will delete posts
    if (
      shouldPostsBeDeletedAfterRemovingCustomPropertyOption(
        propertyId,
        payload.optionId,
        surfacePostsStore.allSortedPosts,
      )
    ) {
      globalConfirmationDialogStore.openConfirmationDialog({
        ...CONFIRM_DELETE_CUSTOM_TEXT_FIELD,
        title: __('Delete "%{customField}" value?', {
          customField:
            singleSelectProperty.selection_options.find((option) => option.id === payload.optionId)?.name ?? '',
        }),
        body: __(
          'This value will be deleted from every post. Posts with only this value selected will be deleted. This action is permanent.',
        ),
        afterConfirmActions: [() => deleteSingleSelectPropertyOption(payload)],
        buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
        xShadow: true,
      })
      return
    } else {
      deleteSingleSelectPropertyOption(payload)
    }
  }

  async function deleteSingleSelectPropertyOption(payload: {
    propertyId: WallCustomPostSingleSelectProperty['id']
    optionId: SingleSelectOption['id']
  }): Promise<void> {
    const propertyId = payload.propertyId
    if (propertyId == null) return

    const singleSelectProperty = wallSingleSelectPropsByCustomPropertyId.value[propertyId]
    if (singleSelectProperty == null) return

    const newOptions = singleSelectProperty.selection_options.filter((option) => option.id !== payload.optionId)

    // First, delete the option from the property
    const updatedCustomPostProperty = await updateCustomPostProperty({
      ...singleSelectProperty,
      selection_options: newOptions,
    })

    // If the update fails, don't update posts that were using the deleted option
    if (updatedCustomPostProperty == null) return

    // Only if deletion succeeds, update posts that were using the deleted option
    // to have undefined for this custom property so they appear in the "No value" group
    const postsToUpdate = surfacePostsStore.allPosts.filter((post) => {
      return post.custom_properties?.[propertyId] === payload.optionId
    })

    postsToUpdate.forEach((post) => {
      const updatedCustomProperties = { ...post.custom_properties }
      updatedCustomProperties[propertyId] = undefined

      surfacePostsStore.updatePostInStore({
        ...post,
        custom_properties: updatedCustomProperties,
      })
    })
  }

  function getSingleSelectPropertyValue(
    customPropertyId: WallCustomPostSingleSelectProperty['id'] | null,
    post: Post,
  ): string | null {
    if (customPropertyId == null) return null
    const postProperty = wallSingleSelectPropsByCustomPropertyId.value[customPropertyId]
    if (postProperty == null) return null
    const propertyValue = post.custom_properties?.[customPropertyId]
    if (propertyValue == null || typeof propertyValue !== 'string') return null
    return propertyValue
  }
  // #endregion

  return {
    // state
    activePostPropertiesSubpanel,
    wallPostProperties,
    previewWallPostProperties,
    attachmentSettingPermissions,
    rawWallCustomPostProperties,
    wallCustomPostProperties,
    isCreatingCustomPostProperty,
    isUpdatingCustomPostProperty,
    isDeletingCustomPostProperty,
    activeCustomPostProperty,

    customLinkButtonProperty,
    rawWallCustomPostPropertiesById,
    wallCustomPostPropertiesById,
    wallSingleSelectPropsByCustomPropertyId,
    wallSingleSelectDefaultsByCustomPropertyId,
    requiredPostProperties,
    subjectPlaceholder,
    bodyPlaceholder,
    isSubjectEnabled,
    attachmentSettings,
    attachmentText,
    attachmentsEnabled,
    isBodyEnabled,
    isColorEnabled,
    isLocationEnabled,
    isDirty,
    isArLensesEnabled,
    arePostPropertiesInvalid,
    shouldHandleDragAndDropToPost,
    shouldHandlePasteToAddAttachment,
    shouldHandleFileUpload,
    shouldHandleLinks,
    requiredWallCustomPostPropertiesById,
    hasFetchedInitialCustomPostProperties,

    // getter functions
    getDisplayedColorForPost,
    isAttachmentTypeEnabled,
    shouldShowPostProperties,

    // actions
    saveSettings,
    showSettingsPanelDirectly,
    closePostPropertiesPanel,
    goBackToPostPropertiesPanel,

    updatePreviewWallPostProperty,
    setAttachmentSettingPermissions,
    fetchAndSetAttachmentSettingPermissions,
    isContentPickerSourcePermitted,
    isAllPermittedAttachmentsEnabled,
    setWallPostProperties,
    setWallPostPropertiesScreenRecorderAttachmentSetting,
    restoreWallPostPropertiesScreenRecorderAttachmentSetting,
    saveSettingsAndGoBackToMainPanel,
    discardChangesAndGoBackToMainPanel,
    discardChangesAndCloseSettingsPanel,

    // Open/close panels
    openedDirectly: readonly(openedDirectly),
    closePostPropertiesSubpanel,
    saveSettingsAndClosePostPropertiesSubpanel,
    discardChangesAndClosePostPropertiesSubpanel,
    xVideoRecorderSettingsPanel: readonly(xVideoRecorderSettingsPanel),
    openVideoRecorderSettingsPanel,
    hideVideoRecorderSettingsPanel,
    xScreenRecorderSettingsPanel: readonly(xScreenRecorderSettingsPanel),
    openScreenRecorderSettingsPanel,
    hideScreenRecorderSettingsPanel,
    xPhotoBoothSettingsPanel: readonly(xPhotoBoothSettingsPanel),
    openPhotoBoothSettingsPanel,
    hidePhotoBoothSettingsPanel,
    commentDrawerZIndexOverride: readonly(commentDrawerZIndexOverride),

    // Video length limits
    maxPossibleVideoLengthLimit,
    wallVideoLengthLimit,
    currentVideoLengthLimit,
    currentVideoLengthLimitSetting,
    updateVideoLengthLimit,

    // Custom post properties
    xCustomPostPropertiesPanel,
    customPropertiesPanelOpenedDirectly: readonly(customPropertiesPanelOpenedDirectly),
    openCustomPostPropertiesPanel,
    closeCustomPostPropertiesPanel,
    createCustomPostProperty,
    updateCustomPostProperty,
    deleteCustomPostProperty,
    showDeleteCustomPropertyWarning,
    setCustomPostProperties,
    insertNewCustomPostProperty,
    removeCustomPostPropertyFromList,
    updateCustomPostPropertyInList,
    moveCustomProperty,
    originalIsScreenRecorderAttachmentSetting,
    fixedCustomPropertyDataType: readonly(fixedCustomPropertyDataType),

    // Single select properties
    hasSingleSelectProperty,
    findSingleSelectPropertyOptionId,
    getSingleSelectPropertyOptionName,
    updateSingleSelectPropertyOptionName,
    updateSingleSelectPropertyOptionAsDefault,
    createSingleSelectPropertyOptionBefore,
    createSingleSelectPropertyOptionAfter,
    moveSingleSelectPropertyOptionUp,
    moveSingleSelectPropertyOptionDown,
    moveSingleSelectPropertyOptionRelativeTo,
    onDeleteSingleSelectPropertyOption,
    getSingleSelectPropertyValue,
  }
})
export {
  MAX_SINGLE_SELECT_OPTION_TEXT_LENGTH,
  MAX_SINGLE_SELECT_OPTIONS,
  MIN_SINGLE_SELECT_OPTIONS,
  PostPropertiesSubpanel,
  SINGLE_SELECT_SORT_INDEX_SPACING,
  useSurfacePostPropertiesStore,
}
